// features/products/pages/ProductStockPage.tsx
/**
 * Product Stock Page - Stock Levels Across Branches
 * 
 * Shows real-time stock levels for a product across all branches.
 * Features:
 * - Stock summary cards (total on hand, available, reserved)
 * - Per-branch breakdown with status indicators
 * - Quick adjust link per branch
 * - Confirmation status display
 */

import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { Card, CardContent, CardHeader } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import {
    getProduct, getProductStockLevels,
    ProductData, ProductStockLevel,
} from '../services/productsApi';
import {
    ArrowLeft, Package, Store, TrendingUp, TrendingDown,
    CheckCircle, Clock, AlertTriangle, Shield,
    Layers, RefreshCw, ExternalLink,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { clsx } from 'clsx';

export function ProductStockPage() {
    const { productId } = useParams<{ productId: string }>();
    const navigate = useNavigate();
    const user = useAuthStore((s) => s.user);

    const { data: product, isLoading: productLoading } = useQuery({
        queryKey: ['product', productId],
        queryFn: () => getProduct(user!.tenantId, productId!),
        enabled: !!user?.tenantId && !!productId,
    });

    const { data: stockLevels, isLoading: stockLoading, refetch } = useQuery({
        queryKey: ['product-stock-levels', productId],
        queryFn: () => getProductStockLevels(user!.tenantId, productId!),
        enabled: !!user?.tenantId && !!productId,
        refetchInterval: 30000, // Auto-refresh every 30s
    });

    const levels = stockLevels || [];

    // Calculate totals
    const totalOnHand = levels.reduce((sum, l) => sum + parseFloat(l.quantityOnHand || '0'), 0);
    const totalAvailable = levels.reduce((sum, l) => sum + parseFloat(l.availableQuantity || '0'), 0);
    const totalReserved = levels.reduce((sum, l) => sum + parseFloat(l.reservedQuantity || '0'), 0);
    const totalBranches = levels.length;
    const branchesWithStock = levels.filter(l => parseFloat(l.quantityOnHand) > 0).length;

    const formatNumber = (val: string | undefined) => {
        if (!val) return '0';
        const num = parseFloat(val);
        return isNaN(num) ? '0' : num.toLocaleString(undefined, { maximumFractionDigits: 0 });
    };

    const getConfirmationBadge = (status: string) => {
        switch (status) {
            case 'confirmed': return <Badge variant="success" size="sm"><Shield className="w-3 h-3 mr-1" />Confirmed</Badge>;
            case 'counted': return <Badge variant="info" size="sm"><CheckCircle className="w-3 h-3 mr-1" />Counted</Badge>;
            default: return <Badge variant="warning" size="sm"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
        }
    };

    if (productLoading) return <div className="flex justify-center py-20"><Spinner size="xl" /></div>;
    if (!product) return <ErrorState title="Product not found" />;

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30">
            {/* Header */}
            <div className="bg-white/90 backdrop-blur-md border-b border-slate-200 sticky top-0 z-30 shadow-sm">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <button onClick={() => navigate(`/products/${productId}`)} className="p-2 rounded-xl hover:bg-slate-100" type="button">
                                <ArrowLeft className="w-5 h-5 text-slate-600" />
                            </button>
                            <div className="p-2.5 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl shadow-lg">
                                <Layers className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <h1 className="text-xl font-bold text-slate-900">{product.name}</h1>
                                <p className="text-sm text-slate-500">
                                    {totalBranches} branch{totalBranches !== 1 ? 'es' : ''} · {branchesWithStock} with stock
                                </p>
                            </div>
                        </div>

                        <div className="flex items-center gap-2">
                            <button onClick={() => navigate(`/inventory/levels/${productId}`)} className="flex items-center gap-1.5 px-3 py-2 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-xl text-xs font-semibold hover:bg-emerald-100 transition-colors" type="button">
                                <ExternalLink className="w-3.5 h-3.5" /> Full Audit & Ledger
                            </button>
                            <button onClick={() => navigate(`/inventory/adjust?productId=${productId}`)} className="flex items-center gap-1.5 px-3 py-2 bg-amber-50 text-amber-700 border border-amber-200 rounded-xl text-xs font-semibold hover:bg-amber-100 transition-colors" type="button">
                                Adjust Stock
                            </button>
                            <button onClick={() => refetch()} className="flex items-center gap-1.5 px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-medium text-slate-700 shadow-sm" type="button">
                                <RefreshCw className="w-3.5 h-3.5" /> Refresh
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-[1400px] mx-auto p-6 lg:p-8 space-y-6">
                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <QuickStat label="Total On Hand" value={formatNumber(String(totalOnHand))} icon={Package} gradient="from-emerald-500 to-teal-600" />
                    <QuickStat label="Available" value={formatNumber(String(totalAvailable))} icon={CheckCircle} gradient="from-blue-500 to-indigo-600" />
                    <QuickStat label="Reserved" value={formatNumber(String(totalReserved))} icon={Shield} gradient="from-amber-500 to-orange-600" />
                    <QuickStat label="Branches" value={totalBranches} icon={Store} gradient="from-purple-500 to-indigo-600" />
                </div>

                {/* Stock Table */}
                {stockLoading ? (
                    <div className="flex justify-center py-12"><Spinner size="lg" /></div>
                ) : levels.length === 0 ? (
                    <EmptyState
                        icon={<Package className="w-16 h-16" />}
                        title="No Stock Data"
                        description="Stock levels will appear here once inventory is received or set up."
                        action={{ label: 'Go to Inventory Setup', onClick: () => navigate('/inventory/setup') }}
                    />
                ) : (
                    <Card className="overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-slate-100">
                                <thead>
                                    <tr className="bg-slate-50/50">
                                        <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase">Branch</th>
                                        <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">On Hand</th>
                                        <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">Available</th>
                                        <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">Reserved</th>
                                        <th className="px-6 py-4 text-center text-xs font-semibold text-slate-500 uppercase">Status</th>
                                        <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">Last Counted</th>
                                        <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-50">
                                    {levels.map((level, idx) => {
                                        const onHand = parseFloat(level.quantityOnHand || '0');
                                        const available = parseFloat(level.availableQuantity || '0');
                                        const isOut = onHand <= 0;
                                        const isLow = onHand > 0 && onHand <= (product.lowStockThreshold || 10);

                                        return (
                                            <motion.tr key={level.branchId} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                                                transition={{ delay: idx * 0.05 }}
                                                className={clsx('hover:bg-slate-50', isOut && 'bg-rose-50/30', isLow && 'bg-amber-50/20')}>
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center gap-3">
                                                        <div className={clsx('w-9 h-9 rounded-xl flex items-center justify-center',
                                                            isOut ? 'bg-rose-100' : isLow ? 'bg-amber-100' : 'bg-emerald-100')}>
                                                            <Store className={clsx('w-4 h-4', isOut ? 'text-rose-500' : isLow ? 'text-amber-500' : 'text-emerald-500')} />
                                                        </div>
                                                        <span className="text-sm font-semibold text-slate-800">{level.branchName || 'Warehouse'}</span>
                                                    </div>
                                                </td>
                                                <td className={clsx('px-6 py-4 text-right text-sm font-bold tabular-nums', isOut ? 'text-rose-600' : isLow ? 'text-amber-600' : 'text-slate-800')}>
                                                    {formatNumber(level.quantityOnHand)}
                                                </td>
                                                <td className="px-6 py-4 text-right text-sm tabular-nums text-slate-600">{formatNumber(level.availableQuantity)}</td>
                                                <td className="px-6 py-4 text-right text-sm tabular-nums text-slate-500">{formatNumber(level.reservedQuantity)}</td>
                                                <td className="px-6 py-4 text-center">{getConfirmationBadge(level.confirmationStatus)}</td>
                                                <td className="px-6 py-4 text-right text-xs text-slate-400">
                                                    {level.lastCountedAt ? new Date(level.lastCountedAt).toLocaleDateString('en-NG') : 'Never'}
                                                </td>
                                                <td className="px-6 py-4 text-right">
                                                    <button
                                                        onClick={() => navigate(`/inventory/adjust?productId=${productId}&branchId=${level.branchId}`)}
                                                        className="text-xs text-emerald-600 hover:text-emerald-700 font-medium"
                                                        type="button"
                                                    >
                                                        Adjust Stock →
                                                    </button>
                                                </td>
                                            </motion.tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </Card>
                )}

                {/* Inventory Settings Link */}
                <Card className="bg-gradient-to-r from-slate-50 to-white border-slate-200">
                    <CardContent className="p-5 flex items-center justify-between">
                        <div>
                            <h3 className="font-semibold text-sm text-slate-800">Inventory Settings</h3>
                            <p className="text-xs text-slate-500 mt-1">
                                Low stock threshold: {product.lowStockThreshold} · Reorder point: {product.reorderPoint || 'Not set'} · Reorder qty: {product.reorderQuantity || 'Not set'}
                            </p>
                        </div>
                        <button onClick={() => navigate(`/products/${productId}/edit`)}
                            className="px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-medium hover:bg-slate-50"
                            type="button">
                            Edit Product Settings
                        </button>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}

function QuickStat({ label, value, icon: Icon, gradient }: {
    label: string; value: string | number; icon: React.ElementType; gradient: string;
}) {
    return (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} whileHover={{ y: -3 }}
            className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 hover:shadow-lg transition-all">
            <div className="flex items-start justify-between">
                <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{label}</p>
                    <p className="text-2xl font-bold text-slate-900 mt-1">{value}</p>
                </div>
                <div className={`p-3 rounded-xl bg-gradient-to-br ${gradient} shadow-lg`}>
                    <Icon className="w-5 h-5 text-white" />
                </div>
            </div>
        </motion.div>
    );
}